import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Upload, Search, Copy, Trash2, ChevronUp, ChevronDown, X, LayoutTemplate, Save, AlertCircle,
} from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import EmptyState from '../EmptyState';
import ConfirmDialog from '../ConfirmDialog';
import EmpleadoPicker from './EmpleadoPicker';
import ImportarPlantillasModal from './ImportarPlantillasModal';
import useEmpleados from './useEmpleados';
import {
  parsePredecesoras, formatPredecesoras, renumerar, quitarActividad, moverActividad, duracionLegible,
} from './utils';
import { BUTTON, FOCUS, ROW } from '../utils/uiTokens';

const PRIORIDADES = [
  { id: 'ninguna', label: 'Sin prioridad' },
  { id: 'baja', label: 'Baja' },
  { id: 'media', label: 'Media' },
  { id: 'alta', label: 'Alta' },
  { id: 'urgente', label: 'Urgente' },
];

const celdaInput = `w-full px-1.5 py-1 text-sm text-slate-800 bg-transparent border border-transparent rounded-md hover:border-slate-200 disabled:hover:border-transparent ${FOCUS.input}`;

// Camino mas largo (dias) con la misma regla que el backend.
const duracionTotal = (actividades) => {
  const por = new Map(actividades.map((a) => [a.orden, a]));
  const fin = new Map();
  const calc = (o, visit) => {
    if (fin.has(o)) return fin.get(o);
    const a = por.get(o);
    if (!a || visit.has(o)) return 0;
    const v = new Set(visit).add(o);
    const ini = a.offset_dias != null && a.offset_dias !== ''
      ? Number(a.offset_dias)
      : Math.max(0, ...(a.predecesoras || []).map((p) => calc(p, v)));
    const f = ini + (Number(a.duracion_dias) || 0);
    fin.set(o, f);
    return f;
  };
  return Math.max(0, ...actividades.map((a) => calc(a.orden, new Set())));
};

const nuevaActividad = (orden, anterior) => ({
  orden,
  titulo: '',
  descripcion: null,
  duracion_dias: 5,
  offset_dias: null,
  prioridad: 'ninguna',
  responsable_empleado_id: null,
  responsable_texto: null,
  predecesoras: anterior ? [anterior] : [],
  hito: null,
});

/**
 * Gestion de plantillas por tipo de proyecto (/proyectos/plantillas).
 * Izquierda: plantillas agrupadas por area. Derecha: editor de la plantilla
 * elegida (datos + tabla de actividades). Solo lideres de proyecto y TI editan;
 * el resto la ve en modo lectura.
 */
const PlantillasView = () => {
  const { empleados } = useEmpleados();
  const [plantillas, setPlantillas] = useState([]);
  const [puedeAdministrar, setPuedeAdministrar] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [selId, setSelId] = useState(null);
  const [borrador, setBorrador] = useState(null); // plantilla en edicion
  const [original, setOriginal] = useState(null); // para detectar cambios
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [importarOpen, setImportarOpen] = useState(false);
  const [confirmar, setConfirmar] = useState(null); // {tipo, payload}

  const cargarLista = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/proyecto-plantillas?inactivas=true');
      setPlantillas(data.plantillas || []);
      setPuedeAdministrar(!!data.puede_administrar);
      return data.plantillas || [];
    } catch (err) {
      setError('No se pudieron cargar las plantillas.');
      return [];
    } finally {
      setCargando(false);
    }
  }, []);

  const abrir = useCallback(async (id) => {
    setError('');
    setAviso('');
    if (!id) {
      setSelId(null); setBorrador(null); setOriginal(null);
      return;
    }
    try {
      const { data } = await axiosInstance.get(`/api/proyecto-plantillas/${id}`);
      const limpio = {
        ...data,
        actividades: (data.actividades || []).map(({ id: _id, responsable, ...a }) => a),
      };
      setSelId(id);
      setBorrador(limpio);
      setOriginal(JSON.stringify(limpio));
    } catch (err) {
      setError('No se pudo abrir la plantilla.');
    }
  }, []);

  useEffect(() => {
    cargarLista().then((lista) => {
      if (lista.length) abrir(lista[0].id);
    });
  }, [cargarLista, abrir]);

  const sucio = borrador && original !== JSON.stringify(borrador);

  // Cambiar de plantilla con cambios sin guardar pide confirmacion.
  const pedirAbrir = (id) => {
    if (id === selId) return;
    if (sucio) setConfirmar({ tipo: 'descartar', payload: id });
    else abrir(id);
  };

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return plantillas.filter(
      (p) => !q || p.nombre.toLowerCase().includes(q) || (p.area || '').toLowerCase().includes(q)
    );
  }, [plantillas, busqueda]);

  const porArea = useMemo(() => {
    const m = new Map();
    filtradas.forEach((p) => {
      const k = p.area || 'Sin área';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b, 'es'));
  }, [filtradas]);

  const areasExistentes = useMemo(
    () => Array.from(new Set(plantillas.map((p) => p.area).filter(Boolean))).sort(),
    [plantillas]
  );

  const setCampo = (campo, valor) => setBorrador((b) => ({ ...b, [campo]: valor }));
  const setActividades = (fn) => setBorrador((b) => ({ ...b, actividades: fn(b.actividades) }));
  const setActividad = (orden, cambios) =>
    setActividades((acts) => acts.map((a) => (a.orden === orden ? { ...a, ...cambios } : a)));

  const crear = async () => {
    if (!puedeAdministrar) return;
    if (sucio) {
      setConfirmar({ tipo: 'descartar', payload: '__nueva__' });
      return;
    }
    await crearNueva();
  };

  const crearNueva = async () => {
    try {
      const { data } = await axiosInstance.post('/api/proyecto-plantillas', {
        nombre: 'Nueva plantilla',
        area: borrador?.area || null,
        actividades: [nuevaActividad(1, null)],
      });
      await cargarLista();
      abrir(data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo crear la plantilla.');
    }
  };

  const guardar = async () => {
    if (!borrador) return;
    setGuardando(true);
    setError('');
    try {
      const actividades = renumerar(
        borrador.actividades.map(({ _predTexto, ...a }) => a).filter((a) => (a.titulo || '').trim())
      );
      const { data } = await axiosInstance.put(`/api/proyecto-plantillas/${borrador.id}`, {
        nombre: borrador.nombre,
        area: borrador.area,
        descripcion: borrador.descripcion,
        activo: borrador.activo,
        actividades,
      });
      const limpio = {
        ...data,
        actividades: (data.actividades || []).map(({ id: _id, responsable, ...a }) => a),
      };
      setBorrador(limpio);
      setOriginal(JSON.stringify(limpio));
      setAviso('Cambios guardados');
      setTimeout(() => setAviso(''), 2500);
      cargarLista();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron guardar los cambios.');
    } finally {
      setGuardando(false);
    }
  };

  const duplicar = async () => {
    try {
      const { data } = await axiosInstance.post(`/api/proyecto-plantillas/${borrador.id}/duplicar`);
      await cargarLista();
      abrir(data.id);
    } catch (err) {
      setError('No se pudo duplicar la plantilla.');
    }
  };

  const eliminar = async () => {
    try {
      await axiosInstance.delete(`/api/proyecto-plantillas/${borrador.id}`);
      const lista = await cargarLista();
      abrir(lista[0]?.id || null);
    } catch (err) {
      setError('No se pudo eliminar la plantilla.');
    }
  };

  const onConfirmar = () => {
    const c = confirmar;
    setConfirmar(null);
    if (!c) return;
    if (c.tipo === 'eliminar') eliminar();
    if (c.tipo === 'descartar') {
      if (c.payload === '__nueva__') {
        setOriginal(JSON.stringify(borrador));
        crearNueva();
      } else abrir(c.payload);
    }
  };

  const ro = !puedeAdministrar;
  const total = borrador ? duracionTotal(borrador.actividades) : 0;
  const hitosExistentes = borrador
    ? Array.from(new Set(borrador.actividades.map((a) => a.hito).filter(Boolean)))
    : [];

  if (!cargando && plantillas.length === 0) {
    return (
      <div className="relative h-full min-h-[480px]">
        <EmptyState
          icon={LayoutTemplate}
          title="Todavía no hay plantillas"
          description="Una plantilla guarda las actividades de un tipo de proyecto para no cargarlas tarea por tarea. Impórtalas desde Excel o créalas aquí."
          action={puedeAdministrar ? (
            <div className="flex gap-2">
              <button type="button" onClick={() => setImportarOpen(true)} className={`${BUTTON.secondary} ${BUTTON.size.md}`}>
                <Upload size={14} /> Importar Excel
              </button>
              <button type="button" onClick={crearNueva} className={`${BUTTON.primary} ${BUTTON.size.md}`}>
                <Plus size={14} /> Nueva plantilla
              </button>
            </div>
          ) : null}
        />
        <ImportarPlantillasModal
          isOpen={importarOpen}
          onClose={() => setImportarOpen(false)}
          onImportado={async () => {
            setImportarOpen(false);
            const lista = await cargarLista();
            if (lista[0]) abrir(lista[0].id);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      {/* Lista */}
      <aside className="w-72 flex-shrink-0 border-r border-slate-200/80 bg-white flex flex-col min-h-0">
        <div className="p-3 space-y-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h1 className="text-sm font-semibold tracking-tight text-slate-900">Plantillas de proyecto</h1>
            {puedeAdministrar && (
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => setImportarOpen(true)}
                  title="Importar desde Excel"
                  aria-label="Importar desde Excel"
                  className={`${BUTTON.ghost} p-1.5`}
                >
                  <Upload size={14} />
                </button>
                <button
                  type="button"
                  onClick={crear}
                  title="Nueva plantilla"
                  aria-label="Nueva plantilla"
                  className={`${BUTTON.ghost} p-1.5`}
                >
                  <Plus size={15} />
                </button>
              </div>
            )}
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar plantilla o área"
              className={`w-full pl-8 pr-3 py-1.5 text-sm bg-slate-100/70 border border-transparent rounded-md placeholder-slate-400 ${FOCUS.input} focus:bg-white`}
            />
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {porArea.map(([area, items]) => (
            <div key={area} className="mb-2">
              <p className="px-3 py-1 text-[11px] font-semibold text-slate-500">{area}</p>
              {items.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pedirAbrir(p.id)}
                  className={`w-full text-left px-3 py-1.5 ${p.id === selId ? ROW.selected : ROW.hover}`}
                >
                  <span className={`block text-sm truncate ${p.activo ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                    {p.nombre}
                  </span>
                  <span className="block text-[11px] text-slate-500 tabular-nums">
                    {p.actividades_count} actividades, {duracionLegible(p.duracion_total_dias)}
                  </span>
                </button>
              ))}
            </div>
          ))}
          {!cargando && filtradas.length === 0 && (
            <p className="px-3 py-4 text-xs text-slate-400">Nada coincide con "{busqueda}".</p>
          )}
        </nav>
      </aside>

      {/* Editor */}
      <section className="flex-1 min-w-0 overflow-y-auto">
        {borrador && (
          <div className="max-w-[1400px] px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[16rem] space-y-1">
                <input
                  value={borrador.nombre}
                  onChange={(e) => setCampo('nombre', e.target.value)}
                  disabled={ro}
                  maxLength={120}
                  aria-label="Nombre de la plantilla"
                  className={`w-full -ml-1.5 px-1.5 py-0.5 text-xl font-semibold tracking-tight text-slate-900 bg-transparent border border-transparent rounded-md hover:border-slate-200 disabled:hover:border-transparent ${FOCUS.input}`}
                />
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                  <label className="flex items-center gap-1">
                    <span>Área</span>
                    <input
                      value={borrador.area || ''}
                      onChange={(e) => setCampo('area', e.target.value)}
                      disabled={ro}
                      list="plantillas-areas"
                      placeholder="Sin área"
                      className={`w-52 px-1.5 py-0.5 text-sm text-slate-700 bg-transparent border border-transparent rounded-md hover:border-slate-200 disabled:hover:border-transparent ${FOCUS.input}`}
                    />
                    <datalist id="plantillas-areas">
                      {areasExistentes.map((a) => <option key={a} value={a} />)}
                    </datalist>
                  </label>
                  <span className="tabular-nums">
                    {borrador.actividades.length} actividades, {duracionLegible(total)} por el camino más largo
                  </span>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!borrador.activo}
                      onChange={(e) => setCampo('activo', e.target.checked)}
                      disabled={ro}
                      className="h-3.5 w-3.5 rounded border-slate-300 accent-cyan-600"
                    />
                    Disponible al crear proyectos
                  </label>
                </div>
              </div>

              {!ro && (
                <div className="flex items-center gap-2">
                  {aviso && <span className="text-xs text-emerald-600">{aviso}</span>}
                  <button type="button" onClick={duplicar} className={`${BUTTON.ghost} ${BUTTON.size.md}`}>
                    <Copy size={14} /> Duplicar
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmar({ tipo: 'eliminar' })}
                    className={`${BUTTON.ghost} ${BUTTON.size.md} hover:text-rose-600`}
                  >
                    <Trash2 size={14} /> Eliminar
                  </button>
                  <button
                    type="button"
                    onClick={guardar}
                    disabled={!sucio || guardando || !borrador.nombre.trim()}
                    className={`${BUTTON.primary} ${BUTTON.size.md}`}
                  >
                    <Save size={14} /> {guardando ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              )}
            </div>

            <textarea
              value={borrador.descripcion || ''}
              onChange={(e) => setCampo('descripcion', e.target.value)}
              disabled={ro}
              rows={2}
              placeholder={ro ? '' : 'Para qué tipo de proyecto sirve (opcional)'}
              className={`mt-3 w-full px-2 py-1.5 text-sm text-slate-600 bg-transparent border border-transparent rounded-md hover:border-slate-200 disabled:hover:border-transparent resize-none ${FOCUS.input}`}
            />

            {error && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200/70 rounded-md">
                <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-rose-700">{error}</span>
              </div>
            )}

            {/* Actividades */}
            <div className="mt-4 bg-white border border-slate-200/80 rounded-xl overflow-x-auto">
              <table className="w-full text-sm border-collapse table-fixed min-w-[1000px]">
                <thead className="bg-slate-50 text-[11px] text-slate-500">
                  <tr>
                    <th className="w-10 py-2 pl-3 pr-1 text-right font-medium">N.º</th>
                    <th className="py-2 px-2 text-left font-medium">Actividad</th>
                    <th className="w-44 py-2 px-2 text-left font-medium">Responsable</th>
                    <th className="w-14 py-2 px-2 text-right font-medium">Días</th>
                    <th className="w-24 py-2 px-2 text-left font-medium" title="Números de las actividades que deben terminar antes">
                      Después de
                    </th>
                    <th className="w-16 py-2 px-2 text-right font-medium" title="Día fijo desde el inicio del proyecto (opcional; ignora las predecesoras)">
                      Día fijo
                    </th>
                    <th className="w-40 py-2 px-2 text-left font-medium">Hito</th>
                    <th className="w-28 py-2 px-2 text-left font-medium">Prioridad</th>
                    {!ro && <th className="w-[84px]" />}
                  </tr>
                </thead>
                <tbody>
                  {borrador.actividades.map((a, i) => (
                    <tr key={`${a.orden}-${i}`} className="border-t border-slate-100 align-top group">
                      <td className="py-2 pl-3 pr-1 text-right text-xs text-slate-400 tabular-nums pt-2.5">{a.orden}</td>
                      <td className="py-1.5 px-2">
                        <input
                          value={a.titulo}
                          onChange={(e) => setActividad(a.orden, { titulo: e.target.value })}
                          disabled={ro}
                          maxLength={200}
                          placeholder="Nombre de la actividad"
                          title={a.titulo}
                          className={celdaInput}
                        />
                      </td>
                      <td className="py-1.5 px-2">
                        <EmpleadoPicker
                          value={a.responsable_empleado_id}
                          onChange={(id) => setActividad(a.orden, { responsable_empleado_id: id })}
                          empleados={empleados}
                          rolTexto={a.responsable_texto}
                          disabled={ro}
                          placeholder="Sin responsable fijo"
                        />
                        {!a.responsable_empleado_id && !ro && (
                          <input
                            value={a.responsable_texto || ''}
                            onChange={(e) => setActividad(a.orden, { responsable_texto: e.target.value || null })}
                            placeholder="o rol: Analista DP"
                            maxLength={120}
                            className="w-full mt-0.5 px-1.5 py-0.5 text-[11px] text-slate-500 bg-transparent border-b border-transparent hover:border-slate-200 focus:outline-none focus:border-cyan-500"
                          />
                        )}
                      </td>
                      <td className="py-1.5 px-2">
                        <input
                          type="number"
                          min={0}
                          value={a.duracion_dias ?? ''}
                          onChange={(e) =>
                            setActividad(a.orden, {
                              duracion_dias: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                            })
                          }
                          disabled={ro}
                          className={`${celdaInput} text-right tabular-nums`}
                        />
                      </td>
                      <td className="py-1.5 px-2">
                        <input
                          value={a._predTexto ?? formatPredecesoras(a.predecesoras)}
                          onChange={(e) => setActividad(a.orden, { _predTexto: e.target.value })}
                          onBlur={(e) =>
                            setActividad(a.orden, {
                              _predTexto: undefined,
                              predecesoras: parsePredecesoras(e.target.value).filter(
                                (p) => p !== a.orden && borrador.actividades.some((x) => x.orden === p)
                              ),
                            })
                          }
                          disabled={ro}
                          placeholder="inicio"
                          className={`${celdaInput} tabular-nums`}
                        />
                      </td>
                      <td className="py-1.5 px-2">
                        <input
                          type="number"
                          min={0}
                          value={a.offset_dias ?? ''}
                          onChange={(e) =>
                            setActividad(a.orden, {
                              offset_dias: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                            })
                          }
                          disabled={ro}
                          placeholder="-"
                          className={`${celdaInput} text-right tabular-nums`}
                        />
                      </td>
                      <td className="py-1.5 px-2">
                        <input
                          value={a.hito || ''}
                          onChange={(e) => setActividad(a.orden, { hito: e.target.value || null })}
                          disabled={ro}
                          list="plantilla-hitos"
                          title={a.hito || undefined}
                          placeholder="-"
                          maxLength={120}
                          className={celdaInput}
                        />
                      </td>
                      <td className="py-1.5 px-2">
                        <select
                          value={a.prioridad || 'ninguna'}
                          onChange={(e) => setActividad(a.orden, { prioridad: e.target.value })}
                          disabled={ro}
                          className={`${celdaInput} pr-6`}
                        >
                          {PRIORIDADES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                      </td>
                      {!ro && (
                        <td className="py-1.5 pr-2">
                          <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => setActividades((acts) => moverActividad(acts, i, -1))}
                              disabled={i === 0}
                              className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30 rounded"
                              aria-label="Subir"
                            >
                              <ChevronUp size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setActividades((acts) => moverActividad(acts, i, 1))}
                              disabled={i === borrador.actividades.length - 1}
                              className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30 rounded"
                              aria-label="Bajar"
                            >
                              <ChevronDown size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setActividades((acts) => quitarActividad(acts, a.orden))}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded"
                              aria-label="Quitar actividad"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <datalist id="plantilla-hitos">
                {hitosExistentes.map((h) => <option key={h} value={h} />)}
              </datalist>
              {!ro && (
                <div className="px-3 py-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() =>
                      setActividades((acts) => {
                        const ultimo = acts.reduce((m, x) => Math.max(m, x.orden), 0);
                        return [...acts, nuevaActividad(ultimo + 1, ultimo || null)];
                      })
                    }
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
                  >
                    <Plus size={13} /> Añadir actividad
                  </button>
                </div>
              )}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Al crear un proyecto, cada actividad arranca cuando terminan las de "Después de" (o en su día fijo) y dura
              sus días. El responsable se asigna a la tarea; si solo hay un rol, se elige la persona al crear el proyecto.
            </p>
          </div>
        )}
      </section>

      <ImportarPlantillasModal
        isOpen={importarOpen}
        onClose={() => setImportarOpen(false)}
        onImportado={async () => {
          setImportarOpen(false);
          const lista = await cargarLista();
          if (!selId && lista[0]) abrir(lista[0].id);
          else if (selId) abrir(selId);
        }}
      />

      <ConfirmDialog
        isOpen={!!confirmar}
        title={confirmar?.tipo === 'eliminar' ? '¿Eliminar esta plantilla?' : '¿Descartar los cambios?'}
        message={
          confirmar?.tipo === 'eliminar'
            ? 'Los proyectos ya creados con ella no cambian. La plantilla no se puede recuperar.'
            : 'Tienes cambios sin guardar en esta plantilla.'
        }
        variant={confirmar?.tipo === 'eliminar' ? 'danger' : 'warning'}
        confirmLabel={confirmar?.tipo === 'eliminar' ? 'Eliminar' : 'Descartar'}
        onConfirm={onConfirmar}
        onClose={() => setConfirmar(null)}
      />
    </div>
  );
};

export default PlantillasView;
